import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  ScrollText, Plus, AlertCircle, CheckCircle, Clock, Eye, Edit, 
  Trash2, Send, User, X, ChevronDown, ChevronUp, Users, Bell, Printer
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { policiesAPI } from '../../services/api';

// Sanitize HTML helper
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

// Print function for policies
const handlePrintPolicy = (policy) => {
  const printContent = `
    <html>
      <head>
        <title>${policy.title} - NC Help Hub Policy</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          .meta { color: #666; margin-bottom: 20px; font-size: 14px; }
          .content { line-height: 1.8; white-space: pre-wrap; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #888; }
          .category { display: inline-block; padding: 4px 12px; background: #f0f0f0; border-radius: 20px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${policy.title}</h1>
        <div class="meta">
          <span class="category">${policy.category?.toUpperCase() || 'GENERAL'}</span>
          &nbsp;&bull;&nbsp; Effective: ${policy.effective_date ? new Date(policy.effective_date).toLocaleDateString() : 'Immediately'}
          &nbsp;&bull;&nbsp; ${policy.requires_signature ? 'Signature Required' : 'No Signature Required'}
        </div>
        <div class="content">${policy.content}</div>
        <div class="footer">
          <p><strong>NC Help Hub LLC</strong> - Non-Medical Services</p>
          <p>Printed on: ${new Date().toLocaleString()}</p>
        </div>
      </body>
    </html>
  `;
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (printWindow) {
    const doc = printWindow.document;
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const body = doc.createElement('body');
    const container = doc.createElement('div');
    container.innerHTML = sanitizeHtml(printContent);
    body.appendChild(container);
    html.appendChild(head);
    html.appendChild(body);
    doc.documentElement.replaceWith(html);
    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
  }
};

const CATEGORY_CONFIG = {
  general: { label: 'General', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]' },
  safety: { label: 'Safety', color: 'bg-[var(--error)]/15 text-red-700' },
  conduct: { label: 'Conduct', color: 'bg-[#F4C2C2]/15 text-purple-700' },
  procedures: { label: 'Procedures', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  benefits: { label: 'Benefits', color: 'bg-[var(--success)]/15 text-green-700' }
};

const PoliciesTab = () => {
  const [policies, setPolicies] = useState([]);
  const [signingStats, setSigningStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedPolicySignatures, setSelectedPolicySignatures] = useState(null);
  const [expandedPolicies, setExpandedPolicies] = useState({});
  const [activeView, setActiveView] = useState('policies'); // 'policies' or 'signatures'
  
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'general',
    is_required_reading: false,
    priority: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [policiesRes, statsRes] = await Promise.all([
        policiesAPI.getAll(null, false), // Get all including inactive
        policiesAPI.getSigningStats()
      ]);
      setPolicies(policiesRes.data || []);
      setSigningStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      if (editingPolicy) {
        await policiesAPI.update(editingPolicy.id, form);
        toast.success('Policy updated');
      } else {
        await policiesAPI.create(form);
        toast.success('Policy created');
      }
      setShowModal(false);
      setEditingPolicy(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await policiesAPI.delete(policyId);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleToggleActive = async (policy) => {
    try {
      await policiesAPI.update(policy.id, { is_active: !policy.is_active });
      toast.success(policy.is_active ? 'Policy deactivated' : 'Policy activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update policy');
    }
  };

  const handleSendReminder = async (policyId, workerId = null) => {
    try {
      await policiesAPI.sendReminder(policyId, workerId);
      toast.success(workerId ? 'Reminder sent to worker' : 'Reminders sent to all unsigned workers');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleViewSignatures = async (policyId) => {
    try {
      const res = await policiesAPI.getSignatures(policyId);
      setSelectedPolicySignatures(res.data);
    } catch (error) {
      toast.error('Failed to load signatures');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      content: '',
      category: 'general',
      is_required_reading: false,
      priority: 0
    });
  };

  const openEditModal = (policy) => {
    setEditingPolicy(policy);
    setForm({
      title: policy.title,
      content: policy.content,
      category: policy.category,
      is_required_reading: policy.is_required_reading,
      priority: policy.priority
    });
    setShowModal(true);
  };

  const toggleExpand = (policyId) => {
    setExpandedPolicies(prev => ({ ...prev, [policyId]: !prev[policyId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Employee Policies</h1>
          <p className="text-[var(--text-muted)] mt-1">Company policies and procedures for workers to read and sign</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingPolicy(null); setShowModal(true); }} 
          className="btn-primary" 
          data-testid="add-policy-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Policy
        </Button>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeView === 'policies' ? 'default' : 'outline'}
          onClick={() => setActiveView('policies')}
          className={activeView === 'policies' ? 'bg-[var(--primary)]' : ''}
        >
          <ScrollText className="w-4 h-4 mr-2" />
          Policies
        </Button>
        <Button
          variant={activeView === 'signatures' ? 'default' : 'outline'}
          onClick={() => setActiveView('signatures')}
          className={activeView === 'signatures' ? 'bg-[var(--primary)]' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Signature Status
        </Button>
      </div>

      {/* Stats Summary */}
      {signingStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{signingStats.summary.total_policies}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Policies</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{signingStats.summary.total_workers}</p>
            <p className="text-sm text-[var(--text-muted)]">Active Workers</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{signingStats.summary.total_signatures}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Signatures</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{signingStats.summary.fully_signed_policies}</p>
            <p className="text-sm text-[var(--text-muted)]">Fully Signed</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{signingStats.summary.workers_fully_compliant}</p>
            <p className="text-sm text-[var(--text-muted)]">Compliant Workers</p>
          </div>
        </div>
      )}

      {/* Policies View */}
      {activeView === 'policies' && (
        <>
          {policies.length === 0 ? (
            <div className="card p-12 text-center">
              <ScrollText className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Policies Yet</h3>
              <p className="text-[var(--text-muted)] mb-4">Create policies for your workers to read and sign</p>
              <Button onClick={() => setShowModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create First Policy
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {policies.map(policy => {
                const policyStats = signingStats?.policy_stats?.find(p => p.policy_id === policy.id);
                const isExpanded = expandedPolicies[policy.id];
                
                return (
                  <div 
                    key={policy.id} 
                    className={`card overflow-hidden ${!policy.is_active ? 'opacity-60' : ''}`}
                    data-testid={`policy-${policy.id}`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-bold">{policy.title}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${CATEGORY_CONFIG[policy.category]?.color || 'bg-[var(--background-subtle)]'}`}>
                              {CATEGORY_CONFIG[policy.category]?.label || policy.category}
                            </span>
                            {policy.is_required_reading && (
                              <span className="text-xs bg-[var(--error)]/15 text-[var(--error)] px-2 py-1 rounded-full">Required</span>
                            )}
                            {!policy.is_active && (
                              <span className="text-xs bg-[var(--background-subtle)] text-[var(--text-secondary)] px-2 py-1 rounded-full">Inactive</span>
                            )}
                          </div>
                          
                          {/* Signature Progress */}
                          {policyStats && policy.is_active && (
                            <div className="mb-4">
                              <div className="flex items-center gap-4">
                                <div className="flex-1">
                                  <div className="w-full bg-[var(--background-subtle)] rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${policyStats.completion_percentage === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                                      style={{ width: `${policyStats.completion_percentage}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-sm font-medium whitespace-nowrap">
                                  {policyStats.signed_count}/{policyStats.total_workers} signed ({policyStats.completion_percentage}%)
                                </span>
                              </div>
                              
                              {policyStats.unsigned_count > 0 && (
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-xs text-amber-600">
                                    Unsigned: {policyStats.unsigned_workers.slice(0, 3).map(w => w.full_name).join(', ')}
                                    {policyStats.unsigned_count > 3 && ` +${policyStats.unsigned_count - 3} more`}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSendReminder(policy.id)}
                                    className="text-amber-600 hover:text-amber-700 text-xs"
                                  >
                                    <Bell className="w-3 h-3 mr-1" />
                                    Remind All ({policyStats.unsigned_count})
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <button 
                            onClick={() => toggleExpand(policy.id)}
                            className="flex items-center gap-1 text-sm text-[var(--primary)] hover:underline"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {isExpanded ? 'Hide Content' : 'Show Content'}
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-1 md:gap-2 mt-2 md:mt-0 ml-0 md:ml-4 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintPolicy(policy)}
                            title="Print Policy"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSignatures(policy.id)}
                          >
                            <Users className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Signatures</span>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(policy)}>
                            {policy.is_active ? <Eye className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(policy)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(policy.id)} className="text-[var(--error)]">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-[var(--background-subtle)] p-6">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--text-secondary)]">
                          {policy.content}
                        </pre>
                        <p className="text-xs text-[var(--text-muted)] mt-4">
                          Created: {new Date(policy.created_at).toLocaleDateString()} • Updated: {new Date(policy.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Signatures Status View */}
      {activeView === 'signatures' && signingStats && (
        <div className="space-y-6">
          {/* By Policy */}
          <div className="card p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Signing Status by Policy
            </h3>
            <div className="space-y-4">
              {signingStats.policy_stats.map(stat => (
                <div key={stat.policy_id} className="border-b border-gray-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium">{stat.policy_title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_CONFIG[stat.category]?.color || 'bg-[var(--background-subtle)]'}`}>
                        {CATEGORY_CONFIG[stat.category]?.label || stat.category}
                      </span>
                      {stat.is_required && (
                        <span className="text-xs bg-[var(--error)]/15 text-[var(--error)] px-2 py-0.5 rounded-full">Required</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${stat.completion_percentage === 100 ? 'text-[var(--success)]' : 'text-amber-600'}`}>
                        {stat.completion_percentage}%
                      </span>
                      {stat.unsigned_count > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendReminder(stat.policy_id)}
                          className="text-amber-600"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Remind ({stat.unsigned_count})
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="w-full bg-[var(--background-subtle)] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${stat.completion_percentage === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                          style={{ width: `${stat.completion_percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-[var(--text-muted)]">{stat.signed_count}/{stat.total_workers}</span>
                  </div>
                  {stat.unsigned_workers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {stat.unsigned_workers.map(worker => (
                        <span 
                          key={worker.id} 
                          className="inline-flex items-center gap-1 text-xs bg-[var(--warning)]/10 text-amber-700 px-2 py-1 rounded-full cursor-pointer hover:bg-amber-100"
                          onClick={() => handleSendReminder(stat.policy_id, worker.id)}
                          title="Click to send reminder"
                        >
                          <Clock className="w-3 h-3" />
                          {worker.full_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* By Worker */}
          <div className="card p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Signing Status by Worker
            </h3>
            <div className="space-y-3">
              {signingStats.worker_stats.map(stat => (
                <div key={stat.worker_id} className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[var(--text-muted)]" />
                    <div>
                      <p className="font-medium">{stat.worker_name}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {stat.signed_count}/{stat.total_policies} policies signed
                        {stat.required_unsigned_count > 0 && (
                          <span className="text-[var(--error)] ml-2">({stat.required_unsigned_count} required pending)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24">
                      <div className="w-full bg-[var(--background-subtle)] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${stat.completion_percentage === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                          style={{ width: `${stat.completion_percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${stat.completion_percentage === 100 ? 'text-[var(--success)]' : 'text-amber-600'}`}>
                      {stat.completion_percentage}%
                    </span>
                    {stat.unsigned_count === 0 ? (
                      <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center">
              <h2 className="text-xl font-bold font-['Cormorant_Garamond']">
                {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => { setShowModal(false); setEditingPolicy(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Policy Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Attendance Policy"
                  className="input-styled mt-1"
                  data-testid="policy-title-input"
                />
              </div>
              
              <div>
                <Label>Content *</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Enter the full policy content here..."
                  className="input-styled mt-1 min-h-[200px]"
                  data-testid="policy-content-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                    <SelectTrigger className="input-styled mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority (higher = more important)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                    className="input-styled mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 bg-[var(--warning)]/10 rounded-lg">
                <Checkbox
                  checked={form.is_required_reading}
                  onCheckedChange={(checked) => setForm({ ...form, is_required_reading: checked })}
                />
                <div>
                  <Label className="font-medium">Required Reading</Label>
                  <p className="text-xs text-[var(--text-muted)]">Workers must sign this policy</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-[var(--border-primary)] flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditingPolicy(null); }}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSubmit} data-testid="save-policy-btn">
                {editingPolicy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Signatures Detail Modal */}
      {selectedPolicySignatures && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold font-['Cormorant_Garamond']">{selectedPolicySignatures.policy_title}</h2>
                <p className="text-sm text-[var(--text-muted)]">
                  {selectedPolicySignatures.signed_count}/{selectedPolicySignatures.total_workers} workers signed
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedPolicySignatures(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6">
              {/* Unsigned Workers */}
              {selectedPolicySignatures.unsigned_workers.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Not Signed ({selectedPolicySignatures.unsigned_count})
                    </h3>
                    <Button
                      size="sm"
                      onClick={() => handleSendReminder(selectedPolicySignatures.policy_id)}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Remind All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selectedPolicySignatures.unsigned_workers.map(worker => (
                      <div key={worker.id} className="flex items-center justify-between p-3 bg-[var(--warning)]/10 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-amber-600" />
                          <div>
                            <p className="font-medium">{worker.full_name}</p>
                            <p className="text-xs text-[var(--text-muted)]">{worker.email}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendReminder(selectedPolicySignatures.policy_id, worker.id)}
                          className="text-amber-600"
                        >
                          <Bell className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Signed Workers */}
              <div>
                <h3 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4" />
                  Signed ({selectedPolicySignatures.signed_count})
                </h3>
                {selectedPolicySignatures.signatures.length === 0 ? (
                  <p className="text-[var(--text-muted)] text-sm">No signatures yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedPolicySignatures.signatures.map(sig => (
                      <div key={sig.id} className="flex items-center justify-between p-3 bg-[var(--success)]/10 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-[var(--success)]" />
                          <div>
                            <p className="font-medium">{sig.worker_name}</p>
                            <p className="text-xs text-[var(--text-muted)]">{sig.worker_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-[var(--success)]">Signed</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {new Date(sig.signed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t border-[var(--border-primary)] flex justify-end">
              <Button onClick={() => setSelectedPolicySignatures(null)}>Close</Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default PoliciesTab;
